/**
 * @description renders the header of the user menu displayed in the application's navigation bar
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { alpha, hexToRgb } from '@mui/system';

import Pill from '@seccon/shared/ui/components/Pill';

import { NavUserMenuIcon } from './NavUserIcon';
import { UserSettingsContext } from '../contexts/userSettings';

const styles = {
  container: {
    backgroundColor: 'primary.dark',
    backgroundImage: (theme) => 'url("data:image/svg+xml,'
      + '<svg id=\'patternId\' width=\'100%\' height=\'100%\' xmlns=\'http://www.w3.org/2000/svg\'>'
      + '<defs>'
      + '<pattern id=\'a\' patternUnits=\'userSpaceOnUse\' width=\'29\' height=\'50.115\' patternTransform=\'scale(2) rotate(0)\'>'
      + `<rect x='0' y='0' width='100%' height='100%' fill='${ hexToRgb(theme.palette.primary.dark) }'/>`
      // eslint-disable-next-line max-len
      + `<path d='M14.498 16.858L0 8.488.002-8.257l14.5-8.374L29-8.26l-.002 16.745zm0 50.06L0 58.548l.002-16.745 14.5-8.373L29 41.8l-.002 16.744zM28.996 41.8l-14.498-8.37.002-16.744L29 8.312l14.498 8.37-.002 16.745zm-29 0l-14.498-8.37.002-16.744L0 8.312l14.498 8.37-.002 16.745z'  stroke-width='1' stroke='${ alpha(theme.palette.common.white, 0.1) }' fill='none' />`
      + '</pattern>'
      + '</defs>'
      + '<rect width=\'800%\' height=\'800%\' transform=\'translate(0,0)\' fill=\'url(%23a)\'/>'
      + '</svg>")',
    backgroundSize: 'cover',
    backgroundPosition: 'bottom center, 50%, 50%'
  },
  infoContainer: {
    alignItems: 'center',
    color: 'primary.contrastText',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '3rem',
    padding: '0.5rem 1rem'
  },
  infoName: {
    lineHeight: 2,
    textAlign: 'center',
    wordBreak: 'break-word'
  },
  infoUsername: {
    marginBottom: '0.25rem',
    textAlign: 'center',
    wordBreak: 'break-word'
  },
  infoRole: {
    backgroundColor: 'primary.dark',
    fontSize: '0.75rem',
    marginBottom: '0.125rem'
  },
  iconContainer: {
    display: 'flex',
    height: 'fit-content',
    justifyContent: 'center',
    position: 'relative'
  },
  iconBackground: {
    backgroundColor: 'common.white',
    bottom: 0,
    display: 'block',
    height: '50%',
    left: 0,
    position: 'absolute',
    width: '100%',
    zIndex: 1
  }
};

/**
 * @prop {string} initials - the initials to display in the user icon
 */
const NavUserMenuHeader = ({ initials }) => {
  // Retrieve the user settings
  const userSettings = React.useContext(UserSettingsContext);

  return (
    <Stack sx={styles.container}>
      <Stack sx={styles.infoContainer}>
        <>
          {userSettings?.firstName && userSettings?.lastName && (
            <Typography variant='h6' sx={styles.infoName}>
              {userSettings.firstName} {userSettings.lastName}
            </Typography>
          )}
          {userSettings?.username && (
            <Typography variant='subtitle2' sx={styles.infoUsername}>
              {userSettings.username}
            </Typography>
          )}
          {userSettings?.userRole?.displayName && (
            <Pill variant='subtitle2' label='Role' sx={styles.infoRole}>
              {userSettings.userRole.displayName}
            </Pill>
          )}
        </>
      </Stack>
      <Box sx={styles.iconContainer}>
        <Box sx={styles.iconBackground} />
        <NavUserMenuIcon>
          {initials}
        </NavUserMenuIcon>
      </Box>
    </Stack>
  )
};

NavUserMenuHeader.propTypes = {
  initials: PropTypes.string
};

NavUserMenuHeader.defaultProps = {
  initials: ''
};

export default NavUserMenuHeader;
